/**
 * RFxcel Analytics API Service
 * Frontend service for RFxcel analytics endpoints
 * Based on business use cases and hierarchy data schema
 */

const API_BASE_URL = import.meta.env.VITE_API_URL || import.meta.env.REACT_APP_API_URL || 'http://localhost:3003/api/v1';

/**
 * Generic fetch wrapper with error handling
 */
const fetchData = async (endpoint, options = {}) => {
  try {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();

    if (!data.success) {
      throw new Error(data.message || 'API request failed');
    }

    return data.data;
  } catch (error) {
    console.error(`RFxcel API Error (${endpoint}):`, error);
    throw error;
  }
};

/**
 * Get dashboard statistics from RFxcel data
 * Business Question: Overall shipment metrics
 */
export const getRfxcelDashboardStats = async (timeRange = '24h') => {
  return fetchData(`/rfxcel-analytics/stats?timeRange=${timeRange}`);
};

/**
 * Get shipment frequency data
 * Business Question: How many shipments are sent per day/week/month? What is the trend over time?
 */
export const getRfxcelShipmentFrequency = async (days = 30, granularity = 'daily') => {
  return fetchData(`/rfxcel-analytics/shipment-frequency?days=${days}&granularity=${granularity}`);
};

/**
 * Get quantity distribution
 * Business Question: What is the average, min, max, and total quantity of items per shipment?
 */
export const getRfxcelQuantityDistribution = async () => {
  return fetchData('/rfxcel-analytics/quantity-distribution');
};

/**
 * Get product mix (top products by shipment frequency)
 * Business Question: What types of products are being shipped most frequently?
 */
export const getRfxcelProductMix = async () => {
  return fetchData('/rfxcel-analytics/product-mix');
};

/**
 * Get shipments by location
 * Business Question: Which locations are shipping or receiving the most?
 */
export const getRfxcelShipmentsByLocation = async (limit = 10) => {
  return fetchData(`/rfxcel-analytics/shipments-by-location?limit=${limit}`);
};

/**
 * Get top partners by shipment volume
 * Business Question: Which owners or trading partners are responsible for the most shipments?
 */
export const getRfxcelTopPartners = async (limit = 5) => {
  return fetchData(`/rfxcel-analytics/top-partners?limit=${limit}`);
};

/**
 * Get shipment details by event ID
 * Business Question: What are the details of a specific shipment (items, products, quantities, etc.)?
 */
export const getRfxcelShipmentDetails = async (eventId) => {
  return fetchData(`/rfxcel-analytics/shipment-details/${eventId}`);
};

/**
 * Get exception analysis
 * Business Question: Were there any failed, voided, or delayed shipments?
 */
export const getRfxcelExceptions = async (days = 7) => {
  return fetchData(`/rfxcel-analytics/exceptions?days=${days}`);
};

/**
 * Get top shipments by quantity
 * Business Question: What are the largest/smallest shipments by quantity or value?
 */
export const getRfxcelTopShipments = async (type = 'largest', limit = 10) => {
  return fetchData(`/rfxcel-analytics/top-shipments?type=${type}&limit=${limit}`);
};

/**
 * Get recent shipments with pagination
 */
export const getRfxcelRecentShipments = async (limit = 10, offset = 0) => {
  return fetchData(`/rfxcel-analytics/recent-shipments?limit=${limit}&offset=${offset}`);
};

/**
 * Get hierarchy summary for shipments
 * Shows aggregated counts of root items, leaf items, and intermediate items
 */
export const getRfxcelHierarchySummary = async (limit = 10) => {
  return fetchData(`/rfxcel-analytics/hierarchy-summary?limit=${limit}`);
};

/**
 * Fetch all dashboard data at once from RFxcel
 * Returns an object with all the data needed for the dashboard
 */
export const fetchAllRfxcelDashboardData = async () => {
  try {
    console.log('Fetching all dashboard data from RFxcel...');

    const [
      stats,
      shipmentFrequency,
      quantityDistribution,
      productMix,
      shipmentsByLocation,
      topPartners,
      exceptions,
      topShipments,
      recentShipments,
      hierarchySummary,
    ] = await Promise.all([
      getRfxcelDashboardStats('24h').catch(() => null),
      getRfxcelShipmentFrequency(30, 'daily').catch(() => null),
      getRfxcelQuantityDistribution().catch(() => null),
      getRfxcelProductMix().catch(() => null),
      getRfxcelShipmentsByLocation(8).catch(() => null),
      getRfxcelTopPartners(5).catch(() => null),
      getRfxcelExceptions(7).catch(() => null),
      getRfxcelTopShipments('largest', 10).catch(() => null),
      getRfxcelRecentShipments(5, 0).catch(() => null),
      getRfxcelHierarchySummary(10).catch(() => null),
    ]);

    return {
      stats,
      shipmentFrequency,
      quantityDistribution: {
        labels: quantityDistribution?.labels || [],
        values: quantityDistribution?.values || []
      },
      productMix: {
        labels: productMix?.labels || [],
        values: productMix?.values || []
      },
      shipmentsByLocation: {
        labels: shipmentsByLocation?.labels || [],
        outbound: shipmentsByLocation?.outbound || [],
        inbound: shipmentsByLocation?.inbound || []
      },
      topPartners: topPartners || [],
      exceptions: {
        labels: exceptions?.labels || [],
        values: exceptions?.values || []
      },
      processingTime: [], // Not applicable for RFxcel
      productTrends: {
        labels: [],
        datasets: []
      }, // Will be populated from product mix
      routePerformance: [], // Not applicable for RFxcel
      systemPerformance: {
        labels: [],
        latency: [],
        throughput: []
      }, // Not applicable for RFxcel
      recentShipments,
      topShipments,
      hierarchySummary,
      source: 'rfxcel'
    };
  } catch (error) {
    console.error('Error fetching RFxcel dashboard data:', error);
    throw error;
  }
};

export default {
  getRfxcelDashboardStats,
  getRfxcelShipmentFrequency,
  getRfxcelQuantityDistribution,
  getRfxcelProductMix,
  getRfxcelShipmentsByLocation,
  getRfxcelTopPartners,
  getRfxcelShipmentDetails,
  getRfxcelExceptions,
  getRfxcelTopShipments,
  getRfxcelRecentShipments,
  getRfxcelHierarchySummary,
  fetchAllRfxcelDashboardData,
};
